import type { SdmEvent } from '../../../data-structures/sdmEventTypes';
import {
  buildDriveOffExit,
  buildLineCrossing,
  buildZoneEntry,
  buildZoneExit,
  nextEntryId,
  randomDurationMs,
  type ZoneEventContext,
} from './eventBuilders';

export interface ProgramOpenEntry {
  id: string;
  tagIds: string[];
  ctx: ZoneEventContext;
  entryMs: number;
  isDriveOff: boolean;
}

export type ProgramKey =
  | 'linesShortGaps'
  | 'fiveConsecutiveZones'
  | 'fiveShortZonesShortGaps'
  | 'fiveShortDriveOffsShortGaps'
  | 'alternateZonesAndDriveOffsShort'
  | 'complexCycleThreeTimes'
  | 'fiftyRandomMixed';

export interface ProgramDescriptor {
  key: ProgramKey;
  label: string;
}

export const PROGRAMS: ProgramDescriptor[] = [
  { key: 'linesShortGaps', label: '5× line crossings, short gaps' },
  { key: 'fiveConsecutiveZones', label: '5× consecutive zone events' },
  { key: 'fiveShortZonesShortGaps', label: '5× short zone events, short gaps' },
  { key: 'fiveShortDriveOffsShortGaps', label: '5× short drive-offs, short gaps' },
  { key: 'alternateZonesAndDriveOffsShort', label: 'Alternate 5 zone × 5 drive-off (short)' },
  { key: 'complexCycleThreeTimes', label: '3× cycle: 5 zone-S, 2 drive-S, 2 zone-L, 1 drive-L' },
  { key: 'fiftyRandomMixed', label: '50× random short/long zone/drive-off, short gaps' },
];

const GAP_MS = 200;
const SHORT_MIN_S = 2;
const SHORT_MAX_S = 15;
const LONG_MIN_S = 30;
const LONG_MAX_S = 120;
const FIXED_CONSECUTIVE_ZONE_MS = 5_000;
// Every program-generated zone event is a queue visit; none of the programs exercise the
// other zone types (dwell, loitering, …) — those are only reachable via the dual presets.
const PROGRAM_ZONE_TYPE = 'queue';

const shortMs = () => randomDurationMs(SHORT_MIN_S, SHORT_MAX_S);
const longMs = () => randomDurationMs(LONG_MIN_S, LONG_MAX_S);

export interface ProgramRunnerDeps {
  /** Sink for emitted events — either client-side processing or backend injection. */
  emit: (event: SdmEvent) => void;
  getTagIds: () => string[];
  /**
   * Builds the (camera, zone) identity for a zone-tracking event. Supplied by the caller so
   * program events resolve to a real camera/polygon when the site has one configured for
   * `tagIds`, exactly like the manual presets do.
   */
  createZoneContext: (tagIds: string[], zoneType: string) => ZoneEventContext;
  /**
   * `entryId` is supplied for zone-tracking entry/exit events (the same id passed to
   * `onOpenEntry`/`onResolveEntry`) so the log line can be formatted consistently with the
   * "Active entries" row it corresponds to. Absent for line-crossing events.
   */
  onEmitEvent: (event: SdmEvent, completed?: boolean, entryId?: string) => void;
  /** Called when a dual event emits its entry. The corresponding exit may or may not fire. */
  onOpenEntry: (entry: ProgramOpenEntry) => void;
  /** Called when a dual event's exit fires naturally. Used to remove the entry from the open list. */
  onResolveEntry: (id: string) => void;
  onProgramEnd: (reason: 'completed' | 'cancelled') => void;
}

export interface ProgramRunner {
  start(key: ProgramKey): void;
  cancel(): void;
}

export function createProgramRunner(deps: ProgramRunnerDeps): ProgramRunner {
  const timers = new Set<ReturnType<typeof setTimeout>>();

  const schedule = (delayMs: number, fn: () => void) => {
    const handle = setTimeout(() => {
      timers.delete(handle);
      fn();
    }, delayMs);
    timers.add(handle);
  };

  const submitEvent = (event: SdmEvent, completed?: boolean, entryId?: string) => {
    deps.emit(event);
    deps.onEmitEvent(event, completed, entryId);
  };

  const clearTimers = () => {
    for (const t of timers) clearTimeout(t);
    timers.clear();
  };

  const finishAfter = (delayMs: number) => {
    schedule(delayMs, () => {
      clearTimers();
      deps.onProgramEnd('completed');
    });
  };

  // Schedule a paired entry+exit zone (or drive-off) event starting `startDelay` from now.
  // Returns the time at which the exit fires (= startDelay + durationMs).
  // Notifies the caller via onOpenEntry/onResolveEntry so an interrupted program leaves
  // its in-flight entries discoverable in the open-entries list, where the user can close
  // them manually via the close buttons.
  const scheduleDualEvent = (
    startDelay: number,
    durationMs: number,
    tagIds: string[],
    isDriveOff: boolean,
  ): number => {
    schedule(startDelay, () => {
      const ctx = deps.createZoneContext(tagIds, PROGRAM_ZONE_TYPE);
      const entryMs = Date.now();
      const id = nextEntryId();
      submitEvent(buildZoneEntry(tagIds, entryMs, ctx), undefined, id);
      deps.onOpenEntry({ id, tagIds, ctx, entryMs, isDriveOff });
      schedule(durationMs, () => {
        const exit = isDriveOff
          ? buildDriveOffExit(tagIds, entryMs, Date.now(), ctx)
          : buildZoneExit(tagIds, entryMs, Date.now(), ctx);
        submitEvent(exit, true, id);
        deps.onResolveEntry(id);
      });
    });
    return startDelay + durationMs;
  };

  // 1) 5 line crossings separated by a short random duration.
  const runLinesShortGaps = () => {
    const tagIds = deps.getTagIds();
    let t = 0;
    for (let i = 0; i < 5; i++) {
      schedule(t, () => submitEvent(buildLineCrossing(tagIds, Date.now())));
      if (i < 4) t += shortMs();
    }
    finishAfter(t + 200);
  };

  // 2) 5 consecutive zone events of a short random duration
  const runFiveConsecutiveZones = () => {
    const tagIds = deps.getTagIds();
    let t = 0;
    for (let i = 0; i < 5; i++) {
      t = scheduleDualEvent(t, FIXED_CONSECUTIVE_ZONE_MS, tagIds, false);
      if (i < 4) t += GAP_MS;
    }
    finishAfter(t + GAP_MS);
  };

  // 3) 5 zone events of short random duration separated by a short random interval.
  const runFiveShortZonesShortGaps = () => {
    const tagIds = deps.getTagIds();
    let t = 0;
    for (let i = 0; i < 5; i++) {
      t = scheduleDualEvent(t, shortMs(), tagIds, false);
      if (i < 4) t += shortMs();
    }
    finishAfter(t + GAP_MS);
  };

  // 4) 5 drive-off events of short random duration separated by a short random interval.
  const runFiveShortDriveOffsShortGaps = () => {
    const tagIds = deps.getTagIds();
    let t = 0;
    for (let i = 0; i < 5; i++) {
      t = scheduleDualEvent(t, shortMs(), tagIds, true);
      if (i < 4) t += shortMs();
    }
    finishAfter(t + GAP_MS);
  };

  // 5) Alternation between 5 zone events and 5 drive-off events, each of a short random
  //    duration separated by a short random interval. 10 events total: z, d, z, d, …
  const runAlternateZonesAndDriveOffsShort = () => {
    const tagIds = deps.getTagIds();
    let t = 0;
    for (let i = 0; i < 10; i++) {
      const isDriveOff = i % 2 === 1;
      t = scheduleDualEvent(t, shortMs(), tagIds, isDriveOff);
      if (i < 9) t += shortMs();
    }
    finishAfter(t + GAP_MS);
  };

  // 6) [5 zone-short, 2 drive-off-short, 2 zone-long, 1 drive-off-long] × 3,
  //    every event separated by short random intervals.
  const runComplexCycleThreeTimes = () => {
    const tagIds = deps.getTagIds();
    type Step = { driveOff: boolean; duration: () => number };
    const cycle: Step[] = [
      ...Array.from({ length: 5 }, () => ({ driveOff: false, duration: shortMs })),
      ...Array.from({ length: 2 }, () => ({ driveOff: true, duration: shortMs })),
      ...Array.from({ length: 2 }, () => ({ driveOff: false, duration: longMs })),
      ...Array.from({ length: 1 }, () => ({ driveOff: true, duration: longMs })),
    ];
    const steps = [...cycle, ...cycle, ...cycle];
    let t = 0;
    steps.forEach((step, idx) => {
      t = scheduleDualEvent(t, step.duration(), tagIds, step.driveOff);
      if (idx < steps.length - 1) t += shortMs();
    });
    finishAfter(t + GAP_MS);
  };

  // 7) Fully random: 50 events, each randomly zone or drive-off, each randomly short or long,
  //    all separated by short random intervals.
  const runFiftyRandomMixed = () => {
    const tagIds = deps.getTagIds();
    let t = 0;
    for (let i = 0; i < 50; i++) {
      const isDriveOff = Math.random() < 0.5;
      const isLong = Math.random() < 0.5;
      const duration = isLong ? longMs() : shortMs();
      t = scheduleDualEvent(t, duration, tagIds, isDriveOff);
      if (i < 49) t += shortMs();
    }
    finishAfter(t + GAP_MS);
  };

  const start = (key: ProgramKey) => {
    cancel(); // defensive: stop any in-flight program first
    switch (key) {
      case 'linesShortGaps':
        runLinesShortGaps();
        break;
      case 'fiveConsecutiveZones':
        runFiveConsecutiveZones();
        break;
      case 'fiveShortZonesShortGaps':
        runFiveShortZonesShortGaps();
        break;
      case 'fiveShortDriveOffsShortGaps':
        runFiveShortDriveOffsShortGaps();
        break;
      case 'alternateZonesAndDriveOffsShort':
        runAlternateZonesAndDriveOffsShort();
        break;
      case 'complexCycleThreeTimes':
        runComplexCycleThreeTimes();
        break;
      case 'fiftyRandomMixed':
        runFiftyRandomMixed();
        break;
    }
  };

  const cancel = () => {
    if (timers.size === 0) return;
    clearTimers();
    deps.onProgramEnd('cancelled');
  };

  return { start, cancel };
}
